<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters justify="space-between">
        <v-col>
          <h3 class="header__title--main">
            {{ $t('message.pages.video.metricsPage.retention') }}
          </h3>
          <br />
          <span
            v-if="lastVideo && lastVideo.title"
            class="text-body-1 newtitle--text font-weight-normal text-truncate"
            :style="'max-width: 100%'"
          >
            {{ lastVideo.title }}
          </span>
        </v-col>
        <v-col v-if="lastVideo && lastVideoRetention">
          <v-row no-gutters justify="end">
            <span class="mr-6">
              <v-skeleton-loader
                v-if="loadings.fetch"
                class="rounded"
                type="chip"
                height="28"
                width="70"
                tile
              ></v-skeleton-loader>
              <template v-else>
                <span class="notSelectedOption--text retentionPercentage">
                  {{ engagementCalc.toFixed(1) }}%
                </span>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="pb-1" icon x-small v-bind="attrs" v-on="on">
                      <v-icon size="18"> mdi-help-circle </v-icon>
                    </v-btn>
                  </template>
                  <v-container class="text-body-2 pa-0">
                    {{
                      $t('message.pages.analytics.retention.engagementExplain')
                    }}
                  </v-container>
                </v-tooltip>
                <br />
              </template>
              <span class="retentionPercentageText">{{
                $t('message.pages.video.metricsPage.engagementCalc')
              }}</span>
            </span>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
    <v-card v-if="noInfoFound" width="1000px" height="300px" flat>
      <empty-state />
    </v-card>
    <template v-else>
      <v-col cols="12" class="px-0">
        <v-col cols="12" class="px-0">
          <v-card
            flat
            v-if="loadings.fetch"
            class="d-flex align-center justify-center px-3"
            height="400px"
            min-width="866px"
          >
            <loading-logo :value="200" />
          </v-card>
          <v-card flat v-else-if="errors.fetchError" class="mt-5 mb-8 px-3">
            <ad-block-error-message
              v-if="isAdBlockEnabled"
              :isLoading="loadings.fetch"
              @handleFetchData="fetchData"
            />
            <v-row
              style="min-height: 250px"
              class="flex-column"
              justify="center"
              align="center"
              no-gutters
              v-else
            >
              <v-card flat height="30" class="mb-4">
                <span class="footerText align-text d-block">
                  {{ errors.shownErrMsg }}
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="18" v-bind="attrs" v-on="on">
                        mdi-help-circle
                      </v-icon>
                    </template>
                    <v-container
                      class="text-body-2 pa-0"
                      style="max-width: 230px"
                    >
                      {{ errors.tooltipErrMsg }}
                    </v-container>
                  </v-tooltip>
                </span>
              </v-card>
              <v-btn color="primary" class="ml-3" @click="fetchData">{{
                $t('message.pages.analytics.tryAgain')
              }}</v-btn>
            </v-row>
          </v-card>
          <v-container
            v-else-if="lastVideo && lastVideoRetention"
            class="pa-0 chart-container"
          >
            <retention-chart-video
              :isExternalSourceVideo="isExternalSourceVideo"
              :retentionChartSeries="retentionChartSeries"
              :m3u8Url="chartBackground.m3u8"
            ></retention-chart-video>
          </v-container>
          <v-col v-else>
            <v-card
              class="d-flex flex-row align-center justify-center"
              width="100%"
              height="360px"
              flat
            >
              <v-icon class="mb-1 mr-1" color="primary">
                mdi-chart-areaspline
              </v-icon>
              <span class="subtitle-2">
                {{ $t('message.pages.analytics.retention.noRelevantInfo') }}
              </span>
            </v-card>
          </v-col>
        </v-col>
        <v-row no-gutters v-if="!errors.fetchError">
          <v-col cols="12">
            <v-row no-gutters>
              <v-col cols="4">
                <v-text-field
                  v-if="!rankLoading"
                  v-model="searchVideoText"
                  dense
                  outlined
                  :label="$t('message.pages.analytics.account.search')"
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  @input="doItemsSearch"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-card class="mt-1" flat width="100%" height="45px">
            <v-row no-gutters v-if="!rankLoading" class="pl-9 pointer">
              <v-col class="d-flex align-center justify-start">
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleOrderByName">
                    <span
                      class="caption mt-1"
                      style="height: 24px; color: var(--v-blueMarine-base)"
                    >
                      <b>{{
                        $t('message.pages.videosDataTable.header.name')
                      }}</b>
                    </span>
                    <v-icon v-if="hover && !nameOrder" color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && nameOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && nameOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-col>
              <v-card
                class="d-flex align-center"
                height="45px"
                flat
                width="138px"
              >
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleOrderByViews" justify="end">
                    <span
                      class="caption mt-1"
                      style="height: 24px; color: var(--v-blueMarine-base)"
                    >
                      <b>{{ $t('message.pages.analytics.account.views') }}</b>
                    </span>
                    <v-icon v-if="hover && !viewsOrder" color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && viewsOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && viewsOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
              <v-card
                class="d-flex align-center"
                height="45px"
                flat
                width="128px"
              >
                <v-hover v-slot="{ hover }">
                  <v-row no-gutters @click="handleOrderByPlays" justify="end">
                    <span
                      class="caption mt-1"
                      style="height: 24px; color: var(--v-blueMarine-base)"
                    >
                      <b>Plays</b>
                    </span>
                    <v-icon v-if="hover && !playsOrder" color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && playsOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && playsOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
              <v-card
                class="d-flex align-center"
                height="45px"
                flat
                width="170px"
              >
                <v-hover v-slot="{ hover }">
                  <v-row
                    no-gutters
                    @click="handleOrderByUniqueViews"
                    justify="end"
                  >
                    <span
                      class="caption mt-1"
                      style="height: 24px; color: var(--v-blueMarine-base)"
                    >
                      <b>{{
                        $t('message.pages.analytics.account.uniqueViews')
                      }}</b>
                    </span>
                    <v-icon
                      v-if="hover && !uniqueViewsOrder"
                      color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && uniqueViewsOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && uniqueViewsOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
              <v-card
                class="d-flex align-center pl-2"
                height="45px"
                flat
                width="150px"
              >
                <v-hover v-slot="{ hover }">
                  <v-row
                    no-gutters
                    @click="handleOrderByUniquePlays"
                    justify="end"
                  >
                    <span
                      class="caption mt-1"
                      style="height: 24px; color: var(--v-blueMarine-base)"
                    >
                      <b>{{
                        $t('message.pages.analytics.account.uniquePlays')
                      }}</b>
                    </span>
                    <v-icon
                      v-if="hover && !uniquePlaysOrder"
                      color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && uniquePlaysOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && uniquePlaysOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
              <v-card
                class="d-flex align-center pl-2"
                height="45px"
                flat
                width="150px"
              >
                <v-hover v-slot="{ hover }">
                  <v-row
                    no-gutters
                    @click="handleOrderByPlayrate"
                    justify="end"
                  >
                    <span
                      class="caption mt-1"
                      style="height: 24px; color: var(--v-blueMarine-base)"
                    >
                      <b>Playrate</b>
                    </span>
                    <v-icon
                      v-if="hover && !playrateOrder"
                      color="grey lighten-1"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && playrateOrder === 'desc'"
                      >mdi-menu-down</v-icon
                    >
                    <v-icon v-if="hover && playrateOrder === 'asc'"
                      >mdi-menu-up</v-icon
                    >
                  </v-row>
                </v-hover>
              </v-card>
            </v-row>
          </v-card>
          <v-col cols="12" class="pa-0 ml-2 pb-4">
            <v-row no-gutters v-if="rankLoading">
              <v-col cols="12">
                <v-skeleton-loader type="table-tbody" />
              </v-col>
            </v-row>
            <vue-virtual-table
              v-else-if="videosData.length"
              class="analyticsRetentionTable pointerTable"
              :config="headers"
              :data="
                searchVideoText.length > 0 ? filteredRankingItems : videosData
              "
              :height="tableHeight"
              :itemHeight="48"
              :minWidth="450"
              @click="updateSelect"
            >
              <template slot-scope="scope" slot="titleCommon">
                <v-col
                  class="d-flex align-center justify-start pa-0"
                  @click="updateSelect(scope.row)"
                >
                  <v-card flat height="25" width="25" color="transparent">
                    <v-icon
                      class="mr-1"
                      color="primary"
                      size="20"
                      v-if="lastVideo && scope.row.id === lastVideo.id"
                    >
                      mdi-check
                    </v-icon>
                  </v-card>
                  <span
                    :title="scope.row.title"
                    class="text-truncate"
                    :style="'max-width: 100%'"
                    >{{ scope.row.title }}</span
                  >
                </v-col>
              </template>
              <template slot-scope="scope" slot="view">
                <v-row
                  no-gutters
                  @click="updateSelect(scope.row)"
                  justify="end"
                >
                  <span>{{
                    scope.row.view
                      ? Number(scope.row.view).toLocaleString()
                      : '0'
                  }}</span>
                </v-row>
              </template>
              <template slot-scope="scope" slot="play">
                <v-row
                  no-gutters
                  @click="updateSelect(scope.row)"
                  justify="end"
                >
                  <span>{{
                    scope.row.play
                      ? Number(scope.row.play).toLocaleString()
                      : '0'
                  }}</span>
                </v-row>
              </template>
              <template slot-scope="scope" slot="uniqueView">
                <v-row
                  no-gutters
                  @click="updateSelect(scope.row)"
                  justify="end"
                >
                  <span>{{
                    scope.row.unique_view
                      ? Number(scope.row.unique_view).toLocaleString()
                      : '0'
                  }}</span>
                </v-row>
              </template>
              <template slot-scope="scope" slot="uniquePlay">
                <v-row
                  no-gutters
                  @click="updateSelect(scope.row)"
                  justify="end"
                >
                  <span>{{
                    scope.row.unique_play
                      ? Number(scope.row.unique_play).toLocaleString()
                      : '0'
                  }}</span>
                </v-row>
              </template>
              <template slot-scope="scope" slot="playrate">
                <v-row no-gutters justify="end" class="pr-1">
                  <span>
                    {{
                      scope.row.playRate
                        ? `${Number(scope.row.playRate)}%`
                        : '-'
                    }}
                  </span>
                </v-row>
              </template>
            </vue-virtual-table>
            <v-card
              v-else
              class="d-flex flex-row align-center justify-center"
              width="1000px"
              height="316px"
              flat
            >
              <v-icon class="mb-1 mr-1" color="primary">
                mdi-folder-search-outline
              </v-icon>
              <span class="subtitle-2">{{
                $t('message.pages.analytics.account.notFound')
              }}</span>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { subDays, isAfter, isBefore, format } from 'date-fns'
import i18n from '@/translations/lang'
import 'vue2-datepicker/locale/pt-br'
import LoadingLogo from '@/components/LoadingLogo.vue'
import { mapState, mapMutations } from 'vuex'
import AdBlockErrorMessage from '@/components/AdBlockErrorMessage.vue'
import VueVirtualTable from 'vue-virtual-table'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import linkHelper from '@/utils/linkHelper.mixin.js'
import realtimeAnalyticsMixin from '@/utils/realtimeAnalytics.mixin.js'
import Fuse from 'fuse.js'
import EmptyState from './EmptyState.vue'
import RetentionChartVideo from './components/RetentionChartVideo.vue'

export default {
  name: 'Retention',
  components: {
    LoadingLogo,
    AdBlockErrorMessage,
    VueVirtualTable,
    RetentionChartVideo,
    EmptyState,
  },
  props: {
    componentData: {
      type: Object,
      default: () => ({ selectedVideos: [], dateRange: [] }),
      required: true,
    },
  },
  mixins: [realtimeAnalyticsMixin, linkHelper],
  data() {
    return {
      isExternalSourceVideo: false,
      lastVideoFrameTime: 0,
      updateInterval: {},
      subMenuIndex: 0,
      playerReady: false,
      chartOptionTypes: [
        this.$t('message.pages.video.metricsPage.retention'),
        this.$t('message.pages.video.metricsPage.viewsPerTime'),
      ],
      chartOption: this.$t('message.pages.video.metricsPage.retention'),
      totalData: {
        views: 0,
        uniqueViews: 0,
        plays: 0,
        playRate: 0,
        uniquePlays: 0,
        retention: null,
      },
      loadings: {
        fetch: true,
      },
      errors: {
        fetch: false,
        fetchError: false,
        shownErrMsg: '',
        tooltipErrMsg: '',
      },
      maxValue: 0,
      retentionChartSeries: [
        {
          name: i18n.t('message.pages.video.metricsPage.retentionRate'),
          data: [],
        },
      ],
      shortcuts: [
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.today'),
          onClick() {
            const date = new Date()
            return [date, date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
          onClick() {
            const date = new Date()

            return [subDays(date, 1), date]
          },
        },
        {
          text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
          onClick() {
            const date = new Date()

            return [subDays(date, 7), date]
          },
        },
      ],
      videosData: [],
      headers: [
        {
          prop: '_action',
          name: 'Nome',
          actionName: 'titleCommon',
        },
        {
          prop: '_action',
          name: 'Visualizações',
          actionName: 'view',
          width: 135,
        },
        {
          prop: '_action',
          name: 'Plays',
          actionName: 'play',
          width: 128,
        },
        {
          prop: '_action',
          name: 'Visualizações Únicas',
          actionName: 'uniqueView',
          width: 170,
        },
        {
          prop: '_action',
          name: 'Plays Únicos',
          actionName: 'uniquePlay',
          width: 151,
        },
        {
          prop: '_action',
          name: 'Playrate',
          actionName: 'playrate',
          width: 150,
        },
      ],
      lastVideo: null,
      lastVideoRetention: null,
      chartBackground: {
        m3u8: null,
      },
      rankLoading: false,
      searchVideoText: '',
      filteredRankingItems: [],
      nameOrder: 'asc',
      viewsOrder: 'asc',
      playsOrder: 'asc',
      uniqueViewsOrder: 'asc',
      uniquePlaysOrder: 'asc',
      playrateOrder: 'asc',
      noInfoFound: false,
    }
  },
  computed: {
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    ...mapState({
      isAdBlock: (state) => state.dashboard.isAdBlockEnabled,
    }),
    tableHeight() {
      if (this.videosData.length < 5) {
        return 288
      } else if (this.videosData.length <= 100) {
        return (this.videosData.length + 1) * 48 + 5
      } else {
        return 4805
      }
    },
    isAdBlockEnabled() {
      if (this.isAdBlock) {
        return this.isAdBlock
      }
      return false
    },
    retentionLink() {
      return this.getRetentionLink()
    },
    maxRangeDate() {
      return subDays(new Date(), 60)
    },
    engagementCalc: {
      get() {
        let engagementCalc
        let maxPlays = Object.keys(this.totalData.retention).length
          ? Object.values(this.totalData.retention).reduce((a, b) => {
              return a > b ? a : b
            })
          : 0
        if (
          this.totalData.retention !== {} &&
          this.totalData.retention !== { 0: 0 }
        ) {
          const totalRetention =
            Object.values(this.totalData.retention).reduce((a, b) => a + b, 0) *
            5
          const videoDuration =
            parseInt(Object.keys(this.totalData.retention).pop()) + 5
          engagementCalc = (totalRetention / maxPlays / videoDuration) * 100
          if (!engagementCalc) engagementCalc = 0
        } else {
          return 0
        }
        return engagementCalc
      },
      cache: false,
    },
    selectedVideos() {
      const { selectedVideos } = this.componentData
      return selectedVideos
    },
    dateRange() {
      const { dateRange } = this.componentData
      return dateRange
    },
  },
  watch: {
    selectedVideos: function () {
      this.handleUpdateRankingTable()
    },
    dateRange: function () {
      let currentVideo = this.lastVideo

      if (this.selectedVideos.length === 1) [currentVideo] = this.selectedVideos

      this.handleUpdateRankingTable()
      this.updateSelect(currentVideo)
    },
  },
  async mounted() {
    this.$root.$emit('checkAdBlock')

    this.$root.$on('analyticsDarkTheme', () => {
      location.reload()
    })
    await this.handleUpdateRankingTable()
    await this.fetchData()
  },
  beforeDestroy() {
    this.emptyRequestLoop()
  },
  methods: {
    ...mapMutations('dashboard', ['UPDATE_IS_ADBLOCK_ENABLED']),
    doItemsSearch() {
      let searchValue = this.searchVideoText
      if (searchValue && searchValue.length > 0) {
        let list = JSON.parse(JSON.stringify(this.videosData))
        let fuseOptions = {
          keys: ['title'],
          includeScore: true,
          threshold: 0.6,
        }
        const fuse = new Fuse(list, fuseOptions)
        let searchResult = fuse.search(searchValue)
        const itemsResults = searchResult.map((result) => result.item)
        this.filteredRankingItems = itemsResults
      } else {
        this.filteredRankingItems = []
      }
    },
    async handleUpdateRankingTable() {
      this.clearErrors()
      try {
        this.noInfoFound = false
        if (this.selectedVideos.length === 0) {
          await this.getAccountRanking()
          return
        }
        this.rankLoading = true
        let videos = []
        for (let video of this.selectedVideos) {
          let ret = await this.fetchVideoAnalytics(video.id)
          videos.push({
            videoExternalId: video.video_external_id,
            play: ret.total.play,
            unique_play: ret.total.unique_play,
            unique_view: ret.total.unique_view,
            view: ret.total.view,
            title: video.title,
            id: video.id,
          })
        }
        this.videosData = [...videos]
        this.videosData.sort((a, b) => b.view - a.view)
        this.viewsOrder = 'desc'
        this.addPlayrate()
        if (!videos.length) this.noInfoFound = true
        this.rankLoading = false
      } catch (err) {
        this.handleFetchError(err)
      }
    },
    async fetchVideoAnalytics(id) {
      try {
        let ret = await newAnalyticsApi.getSingleVideoAnalytics(
          id,
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        return ret.data
      } catch (e) {
        console.log('e', e)
      }
    },
    addPlayrate() {
      this.videosData = this.videosData.map((video) => {
        let playRate = ((video.unique_play * 100) / video.unique_view).toFixed(
          1
        )
        return {
          ...video,
          playRate: playRate ?? '-',
        }
      })
    },
    handleFetchError(err) {
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ??
        err.response.data.errMsg ??
        err.response.data.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
      this.errors.fetch = true
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
    async getAccountRanking() {
      this.rankLoading = true
      this.wasFetch = false
      this.clearErrors()
      try {
        let ret = await newAnalyticsApi.getRanking(
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        this.videosData = ret.data
        if (!ret.data.length) {
          this.noInfoFound = true
          return
        }
        this.videosData.sort((a, b) => b.view - a.view)
        this.viewsOrder = 'desc'
        this.addPlayrate()
      } catch (e) {
        this.rankLoading = false
        this.handleFetchError(e)
      }
      this.wasFetch = true
      this.rankLoading = false
    },
    async updateSelect(video) {
      this.loadings.fetch = true
      await this.updateDataV2(video)
      this.loadings.fetch = false
    },
    changeUpdateTime() {
      this.emptyRequestLoop()
      this.updateInterval = setInterval(() => {
        let video = this.lastVideo || this.selectedVideos[0]
        this.updateDataV2(video)
      }, 30000)
    },
    emptyRequestLoop() {
      window.clearInterval(this.updateInterval)
      this.updateInterval = {}
    },
    verifyDisabledDate(date) {
      // 2022-10-04 é quando começou o analytics novo
      if (isBefore(date, new Date('2022-10-04'))) return false
      const canUse =
        isAfter(this.maxRangeDate, date) || !isBefore(date, new Date())
      return canUse
    },
    aggregateRetention(data) {
      if (this.maxValue === 0) {
        if (Math.max(...Object.values(data)) === 0) {
          this.maxValue = 10
        } else {
          this.maxValue = Math.max(...Object.values(data))
        }
      }

      if (Object.keys(data).length <= 1) {
        const videoSize = this.$store.state.video.video.length
        for (let i = 0; i <= videoSize + 5; i += 5) {
          const missingToVideoSize = videoSize - i
          data[i] = 0
          if (missingToVideoSize < 5) {
            data[missingToVideoSize] = 0
            break
          }
        }
      }

      return Object.keys(data)
        .map((item) => ({
          x: item,
          y: data[item],
        }))
        .sort(function (a, b) {
          return a.x - b.x
        })
    },
    async fetchData() {
      this.errors.fetch = false
      this.loadings.fetch = true
      this.lastVideo = null
      if (this.videosData[0]) {
        await this.updateDataV2(this.videosData[0])
      }
      this.loadings.fetch = false
    },
    async updateDataV2(video) {
      this.clearErrors()
      this.errors.fetch = false
      this.lastVideo = video
      try {
        const { data } = await newAnalyticsApi.getVideoMetricsV2(
          video.id,
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        this.isExternalSourceVideo = video.hasOwnProperty('video_external_id')
          ? video.video_external_id.split('-')[0] === '65787465'
          : false
        this.chartBackground.m3u8 = data.video.hls
        this.lastVideoRetention = data
        this.UPDATE_IS_ADBLOCK_ENABLED(false)
        this.totalData.retention = data.retention
        this.retentionChartSeries = [
          {
            name: this.$t('message.pages.video.metricsPage.retentionRate'),
            data: this.aggregateRetention(data.retention),
          },
          {
            name: this.$t('message.pages.video.metricsPage.viewsPerTime'),
            data: this.aggregateRetention(data.views_data),
          },
        ]
      } catch (err) {
        this.handleFetchError(err)
      }
    },
    handleOrderByName() {
      if (!this.nameOrder || this.nameOrder === 'desc') {
        this.nameOrder = 'asc'
      } else {
        this.nameOrder = 'desc'
      }
      if (this.nameOrder === 'asc') {
        this.videosData.sort((a, b) =>
          a.title.localeCompare(b.title, undefined, { numeric: true })
        )
      }
      if (this.nameOrder === 'desc') {
        this.videosData.sort(
          (a, b) =>
            -a.title.localeCompare(b.title, undefined, { numeric: true })
        )
      }
    },
    handleOrderByViews() {
      if (!this.viewsOrder || this.viewsOrder === 'desc') {
        this.viewsOrder = 'asc'
      } else {
        this.viewsOrder = 'desc'
      }
      if (this.viewsOrder === 'asc') {
        this.videosData.sort((a, b) => a.view - b.view)
      }
      if (this.viewsOrder === 'desc') {
        this.videosData.sort((a, b) => b.view - a.view)
      }
      if (this.searchVideoText) this.doItemsSearch()
    },
    handleOrderByPlays() {
      if (!this.playsOrder || this.playsOrder === 'desc') {
        this.playsOrder = 'asc'
      } else {
        this.playsOrder = 'desc'
      }
      if (this.playsOrder === 'asc') {
        this.videosData.sort((a, b) => a.play - b.play)
      }
      if (this.playsOrder === 'desc') {
        this.videosData.sort((a, b) => b.play - a.play)
      }
      if (this.searchVideoText) this.doItemsSearch()
    },
    handleOrderByUniqueViews() {
      if (!this.uniqueViewsOrder || this.uniqueViewsOrder === 'desc') {
        this.uniqueViewsOrder = 'asc'
      } else {
        this.uniqueViewsOrder = 'desc'
      }
      if (this.uniqueViewsOrder === 'asc') {
        this.videosData.sort((a, b) => a.unique_view - b.unique_view)
      }
      if (this.uniqueViewsOrder === 'desc') {
        this.videosData.sort((a, b) => b.unique_view - a.unique_view)
      }
      if (this.searchVideoText) this.doItemsSearch()
    },
    handleOrderByUniquePlays() {
      if (!this.uniquePlaysOrder || this.uniquePlaysOrder === 'desc') {
        this.uniquePlaysOrder = 'asc'
      } else {
        this.uniquePlaysOrder = 'desc'
      }
      if (this.uniquePlaysOrder === 'asc') {
        this.videosData.sort((a, b) => a.unique_play - b.unique_play)
      }
      if (this.uniquePlaysOrder === 'desc') {
        this.videosData.sort((a, b) => b.unique_play - a.unique_play)
      }
      if (this.searchVideoText) this.doItemsSearch()
    },
    handleOrderByPlayrate() {
      if (!this.playrateOrder || this.playrateOrder === 'desc') {
        this.playrateOrder = 'asc'
      } else {
        this.playrateOrder = 'desc'
      }
      if (this.playrateOrder === 'asc') {
        this.videosData.sort((a, b) => a.playRate - b.playRate)
      }
      if (this.playrateOrder === 'desc') {
        this.videosData.sort((a, b) => b.playRate - a.playRate)
      }
      if (this.searchVideoText) this.doItemsSearch()
    },
  },
}
</script>

<style lang="scss">
.v-tooltip__content {
  opacity: 0.9 !important;
  background-color: rgb(76, 76, 76) !important;
}

@import '~vue2-datepicker/scss/index.scss';
.svgSize {
  width: 450px;
}

.main-scroll {
  border: none !important;
}

.navText {
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 150% !important;
}

.analyticsRetentionTable .header-cell {
  justify-content: start !important;
  padding-left: 5px !important;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
}

.analyticsRetentionTable .item-cell {
  border-bottom: 0px !important;
  border-top: 1px solid var(--v-contrast-base);
}

.analyticsRetentionTable .header-cell:first-child .header-cell-inner {
  padding-left: 25px !important;
}

.analyticsRetentionTable .item-cell-inner {
  padding-left: 0px !important;
}

.analyticsRetentionTable .t-header {
  display: none;
}

.analyticsRetentionTable {
  overflow-x: hidden !important;
  background-color: var(--v-dark-newBackground);
}

.bg-dark .analyticsRetentionTable .item-line {
  background-color: var(--v-neutral-darken2) !important;
}

.darkThemeSvgFix {
  svg text {
    fill: white;
  }
  .apexcharts-legend-text {
    color: white !important;
  }
}

.retentionPercentage {
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
}

.retentionPercentageText {
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: #585d83;
}

.pointerTable {
  cursor: pointer;
}
</style>
